/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import DateTimeCard from "../booking/DateTimeCard";
import LocationSummary from "../LocationSummary";
import ProviderCard from "../booking/ProviderCard";
import ServicesCard from "../booking/ServicesCard";
import Button from "../ui/Button";
import ButtonGroup from "../ui/ButtonGroup";
import StatusRibbon from "../ui/StatusRibbon";
import {
  SectionedCard,
  SectionedCardHeader,
  SectionedCardBody,
  SectionedCardFooter,
} from "../ui/SectionedCard";
import { ROUTES } from "../../constants/constants";
import { fetchWaitingListItem } from "../../lib/react-query/useWaitingList";
import { useServices } from "../../lib/react-query/useServices";
import { useBooking } from "../../hooks/useBooking";
import { setFlow, FlowType } from "../../store/slices/flowSlice";
import getWaitingListStatusConfig from "../../utils/getWaitingListStatusConfig";
import { useAnyUnit } from "../../lib/react-query/useAnyUnit";
import usePluginUtils from "../../hooks/usePluginUtils";
import WaitingListCancelModal from "./WaitingListCancelModal";
import { useToast } from "../ui/Toast";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const WaitingListItem = ({ entry }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { setSelectedServices, setSelectedProvider, setSelectedLocation, setSelectedDate, resetBookingRequest } =
    useBooking();
  const [isVerifyingSession, setIsVerifyingSession] = useState(false);
  const { toast } = useToast();
  const { isPluginEnabled } = usePluginUtils();
  const { data: anyUnit } = useAnyUnit({
    enabled: isPluginEnabled("any_unit"),
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const hideProvider = !!anyUnit?.hide_other_providers;
  const config = getWaitingListStatusConfig(entry);
  const { data: publicServices } = useServices(
    { "filter[mark_combine]": true },
    { enabled: config?.isSlotAvailable === true }
  );

  if (!config) return null;

  const isSlotAvailable = config.isSlotAvailable === true;
  const service = entry.service
    ? { ...entry.service, group_booking_count: entry.count > 1 ? entry.count : null }
    : null;
  const services = service ? [service] : [];

  // The click otherwise touches no network, so an expired session would only surface on the
  // DateTime page — after the flow was seeded and ProtectedRoute unmounted. Ask the server first.
  const isSessionAlive = async () => {
    try {
      await fetchWaitingListItem(queryClient, entry.id);
      return true;
    } catch (e) {
      const status = e?.response?.status;
      
      if (status !== 401 && status !== 419) {
        toast({ description: extractErrorMessage(e, "Something went wrong"), variant: "error" });
      }
      return false;
    }
  };

  const handleCtaClick = async () => {
    if (isSlotAvailable) {
      setIsVerifyingSession(true);
      const sessionAlive = await isSessionAlive();
      setIsVerifyingSession(false);

      if (!sessionAlive) return;
    }

    const publicService = service && publicServices?.find((s) => +s.id === +service.id);
    const bookingServices = publicService
      ? [{ ...publicService, group_booking_count: service.group_booking_count }]
      : services;

    resetBookingRequest();
    setSelectedServices(bookingServices);
    setSelectedProvider(entry.provider);
    setSelectedLocation(entry.location);

    if (!isSlotAvailable) {
      navigate(ROUTES.HOME);
      return;
    }

    setSelectedDate(entry.date);
    dispatch(setFlow(FlowType.BOOKING));

    const hasAddonsStep = isPluginEnabled("paid_attributes") && bookingServices.some((s) => s.has_available_addons);

    navigate(hasAddonsStep ? ROUTES.ADDONS : ROUTES.DATETIME);
  };

  return (
    <SectionedCard $tone={isSlotAvailable ? "accent" : undefined}>
      <StatusRibbon tone={config.tone} title={config.label} />
      <SectionedCardHeader>
        <DateTimeCard date={entry.date} timeLabel="Any open time slot" showIcon={false} />
      </SectionedCardHeader>
      <SectionedCardBody $gap="1rem">
        <LocationSummary location={entry.location} />
        {!hideProvider && <ProviderCard provider={entry.provider} />}
        <ServicesCard services={services} />
      </SectionedCardBody>
      {(config.cta || entry.is_cancellable) && (
        <SectionedCardFooter>
          <ButtonGroup itemsWidth={config.cta && entry.is_cancellable ? "half" : "full"}>
            {config.cta && (
              <Button
                variant="primary"
                title={config.cta}
                onClick={handleCtaClick}
                loading={isVerifyingSession}
              />
            )}
            {entry.is_cancellable && (
              <WaitingListCancelModal
                entryId={entry.id}
                trigger={<Button variant="outline" title="Cancel" />}
              />
            )}
          </ButtonGroup>
        </SectionedCardFooter>
      )}
    </SectionedCard>
  );
};

export default WaitingListItem;
